import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";

import DOMPurify from "dompurify";

const API = process.env.REACT_APP_BACKEND_URL;

const categories = ["All", "Business", "Insurance", "Finance", "Leadership", "Wellness"];

const articleStyle = { fontSize: "1.05rem", lineHeight: 1.9, color: "var(--text)" };

/* ─── Blog Post Detail ─────────────────────────────────────── */
const BlogPostDetail = ({ slug }) => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/blog/${slug}`)
      .then(r => setPost(r.data))
      .catch(() => { setPost(null); })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <main>
      <div className="pg-page-hero"><div className="pg-container"><h1>Loading...</h1></div></div>
    </main>
  );

  if (!post) return (
    <main>
      <div className="pg-page-hero"><div className="pg-container"><h1>Post Not Found</h1></div></div>
      <section className="pg-section"><div className="pg-container pg-text-center">
        <p style={{ color: "var(--muted)" }}>The article you're looking for doesn't exist or has been unpublished.</p>
        <Link to="/blog" className="pg-btn pg-btn-primary" style={{ marginTop: "1rem" }}>Back to Blog</Link>
      </div></section>
    </main>
  );

  return (
    <main data-testid="blog-post-page">
      <div className="pg-page-hero">
        <div className="pg-container">
          <span className="pg-label">{post.category}</span>
          <h1>{post.title}</h1>
          <p>By {post.author} &middot; {new Date(post.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
        </div>
      </div>

      <section className="pg-section">
        <div className="pg-container" style={{ maxWidth: 800, margin: "0 auto" }}>
          <Link to="/blog" style={{ display: "inline-flex", alignItems: "center", gap: ".5rem", color: "var(--magenta)", fontWeight: 600, marginBottom: "2rem", fontSize: ".9rem" }}>
            <i className="fas fa-arrow-left" /> Back to Blog
          </Link>

          {post.featured_image && (
            <img src={post.featured_image} alt={post.title} style={{ width: "100%", borderRadius: 16, marginBottom: "2rem", maxHeight: 400, objectFit: "cover" }} />
          )}

          <article
            className="blog-content"
            style={articleStyle}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
          />

          <div style={{ marginTop: "3rem", padding: "2rem", background: "var(--bg-pink)", borderRadius: 16, textAlign: "center" }}>
            <h3>Ready to Take the Next Step?</h3>
            <p style={{ color: "var(--muted)" }}>Schedule a free consultation to discuss how we can help your business.</p>
            <Link to="/book" className="pg-btn pg-btn-primary" style={{ marginTop: ".5rem" }}>Book a Call</Link>
          </div>
        </div>
      </section>
    </main>
  );
};

/* ─── Blog Listing ─────────────────────────────────────────── */
export const BlogPage = () => {
  const { slug } = useParams();
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/blog`)
      .then(r => setPosts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (slug) return <BlogPostDetail slug={slug} />;

  const filtered = filter === "All" ? posts : posts.filter(p => p.category === filter);

  return (
    <main data-testid="blog-page">
      <div className="pg-page-hero">
        <div className="pg-container">
          <span className="pg-label">Insights & Articles</span>
          <h1>The Paradigm Blog</h1>
          <p>Business tips, success stories, and entrepreneurial insights from our team</p>
        </div>
      </div>

      <section className="pg-section">
        <div className="pg-container">
          {/* Category Filter */}
          <div style={{ display: "flex", gap: ".5rem", justifyContent: "center", marginBottom: "2.5rem", flexWrap: "wrap" }}>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                data-testid={`blog-filter-${cat.toLowerCase()}`}
                style={{
                  padding: ".5rem 1.2rem", borderRadius: 50, border: "none",
                  background: filter === cat ? "var(--grad)" : "var(--bg-light)",
                  color: filter === cat ? "#fff" : "var(--text)",
                  fontWeight: 600, cursor: "pointer", fontSize: ".85rem",
                  fontFamily: "'Inter', sans-serif", transition: "all .2s"
                }}
              >
                {cat}
              </button>
            ))}
          </div>

          {(() => {
            if (loading) {
              return (
                <div className="pg-text-center" style={{ padding: "3rem" }}>
                  <i className="fas fa-spinner fa-spin" style={{ fontSize: "2rem", color: "var(--magenta)" }} />
                </div>
              );
            }
            if (filtered.length === 0) {
              return (
                <div className="pg-text-center" style={{ padding: "4rem 1rem" }}>
                  <i className="fas fa-newspaper" style={{ fontSize: "3rem", color: "var(--border)", marginBottom: "1rem", display: "block" }} />
                  <h3 style={{ color: "var(--muted)" }}>No Articles Yet</h3>
                  <p style={{ color: "var(--muted)" }}>Check back soon for business insights and thought leadership articles.</p>
                </div>
              );
            }
            return (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: "2rem" }}>
                {filtered.map(post => (
                  <Link to={`/blog/${post.slug}`} key={post.id} style={{ textDecoration: "none" }}>
                    <div data-testid={`blog-card-${post.slug}`} style={{
                      background: "#fff", borderRadius: 16, overflow: "hidden",
                      border: "1px solid var(--border)", transition: "all .3s",
                      boxShadow: "0 2px 12px rgba(0,0,0,.06)"
                    }}>
                      {post.featured_image ? (
                        <img src={post.featured_image} alt={post.title} style={{ width: "100%", height: 200, objectFit: "cover" }} />
                      ) : (
                        <div style={{ height: 200, background: "var(--grad)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <i className="fas fa-pen-fancy" style={{ fontSize: "2.5rem", color: "rgba(255,255,255,.5)" }} />
                        </div>
                      )}
                      <div style={{ padding: "1.4rem" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: ".5rem", marginBottom: ".6rem" }}>
                          <span style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--magenta)", textTransform: "uppercase", letterSpacing: ".08em" }}>{post.category}</span>
                          <span style={{ fontSize: ".75rem", color: "var(--muted)" }}>&middot; {new Date(post.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                        </div>
                        <h3 style={{ fontSize: "1.15rem", marginBottom: ".5rem", color: "var(--black)" }}>{post.title}</h3>
                        {post.excerpt && <p style={{ fontSize: ".9rem", color: "var(--muted)", margin: 0, lineHeight: 1.5 }}>{post.excerpt}</p>}
                        <div style={{ marginTop: ".8rem", fontSize: ".82rem", color: "var(--magenta)", fontWeight: 600 }}>
                          Read Article <i className="fas fa-arrow-right" style={{ fontSize: ".7rem" }} />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            );
          })()}
        </div>
      </section>

      <section className="pg-cta-banner">
        <div className="pg-container">
          <h2>Want Personalized Business Advice?</h2>
          <p>Our blog shares general insights, but every business is unique. Book a free call for tailored guidance.</p>
          <div className="pg-cta-btns">
            <Link to="/book" className="pg-btn pg-btn-white"><i className="fas fa-calendar-check" /> Book a Call</Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default BlogPage;
